import {type ChangeEvent, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import type {Walk} from 'types/payload-types';

/**
 * Hook für die Walks-Übersichtsseite. Kapselt Suche und Stadtfilter.
 *
 * @param walks Array aller Walks.
 * @returns Gefiltertes Walk-Array und Event-Handler.
 */
export const useWalksPage = (walks: Walk[]) => {
    const [search, setSearch] = useState('');
    const [cityFilter, setCityFilter] = useState('');
    const {t} = useTranslation();

    const filteredWalks = useMemo(() => {
        const term = search.toLowerCase();

        return walks.filter(walk => {
            const matchesSearch = !term
                || walk.title.toLowerCase().includes(term)
                || walk.shortDescription.toLowerCase().includes(term);

            const walkCityId = typeof walk.city === 'string' ? walk.city : walk.city.id;
            const matchesCity = !cityFilter || walkCityId === cityFilter;

            return matchesSearch && matchesCity;
        });
    }, [walks, search, cityFilter]);

    /**
     * Handler für das Suchfeld.
     *
     * @param e Das Change-Event des Inputs.
     */
    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
    };

    /**
     * Handler für den Stadtfilter.
     *
     * @param e Das Change-Event des Selects.
     */
    const handleCityFilter = (e: ChangeEvent<HTMLSelectElement>) => {
        setCityFilter(e.target.value);
    };

    return {
        cityFilter,
        filteredWalks,
        handleCityFilter,
        handleSearch,
        search,
        t
    };
};